/**
 * Advice linked to Fact Find rows by id as well as by position (Stage 4 of
 * docs/plans/ADVICE_PLAN_AND_DOCUMENT_INGESTION_PLAN.md, owner-approved Oct 21 2026: additive).
 *
 * Advice still points at funds and covers by position (reviewRowRemap.js lists the links). Each
 * entry now also records the id of its row:
 *   insuranceActions[i].coverId · superRollovers[i].fundId · coverChangeExplanations['current_i'].coverId
 *   a retained row's originalCoverId · a transferred row's transferredFromCoverId
 * (fundProjections.currentPortfolioByFund[i] already carries fundId.)
 *
 *   stamp — writers add the ids of the rows the positions point at now (never overwrite one).
 *   align — readers put entries back over the rows their ids name, when the rows moved without
 *           the advice (a write path that didn't realign). Entries without ids keep their position;
 *           when none of the recorded ids exist any more (the rows were re-keyed) or the ids
 *           disagree, nothing moves — exactly as before ids were recorded.
 *
 * Shared by browser and server: CommonJS body, no object spread.
 */

var remapping = require('./reviewRowRemap');

var CURRENT_KEY = /^current_(\d+)(.*)$/;

function idOf(row) {
  return row && row.id != null && row.id !== '' ? String(row.id) : null;
}

function rowsOf(clientData) {
  var data = clientData || {};
  return {
    covers: (data.insurance && data.insurance.covers) || [],
    funds: (data.superannuation && data.superannuation.currentFunds) || [],
  };
}

function coversKeyOf(ns) {
  return ns === 'amendments' ? 'recommendedInsuranceCovers' : 'recommendedCovers';
}

// ---- stamp ----

function stampList(list, rows, field) {
  if (!Array.isArray(list)) return list;
  var changed = false;
  var out = list.map(function (entry, i) {
    var id = idOf(rows[i]);
    if (!entry || typeof entry !== 'object' || entry[field] != null || !id) return entry;
    changed = true;
    var next = Object.assign({}, entry);
    next[field] = rows[i].id;
    return next;
  });
  return changed ? out : list;
}

function stampCoverRows(list, covers) {
  if (!Array.isArray(list)) return list;
  var changed = false;
  var out = list.map(function (row) {
    if (!row || typeof row !== 'object') return row;
    if (row.isRetained === true && row.originalCoverIndex != null && row.originalCoverId == null) {
      var held = covers[Number(row.originalCoverIndex)];
      if (idOf(held)) { changed = true; return Object.assign({}, row, { originalCoverId: held.id }); }
    }
    var t = remapping.transferIndexOf(row);
    if (t != null && row.transferredFromCoverId == null && idOf(covers[t])) {
      changed = true;
      return Object.assign({}, row, { transferredFromCoverId: covers[t].id });
    }
    return row;
  });
  return changed ? out : list;
}

function stampExplanations(map, covers) {
  if (!map || typeof map !== 'object') return map;
  var changed = false;
  var out = {};
  Object.keys(map).forEach(function (key) {
    var entry = map[key];
    var m = CURRENT_KEY.exec(key);
    var cover = m && !m[2] ? covers[Number(m[1])] : null;
    if (cover && idOf(cover) && entry && typeof entry === 'object' && !Array.isArray(entry) && entry.coverId == null) {
      changed = true;
      out[key] = Object.assign({}, entry, { coverId: cover.id });
    } else {
      out[key] = entry;
    }
  });
  return changed ? out : map;
}

/** One advice object (recommendations or amendments) with its row ids recorded; the same object when nothing was added. */
function stampAdvice(advice, rows, ns) {
  if (!advice || typeof advice !== 'object') return advice;
  var key = coversKeyOf(ns);
  var patch = {};
  var actions = stampList(advice.insuranceActions, rows.covers, 'coverId');
  if (actions !== advice.insuranceActions) patch.insuranceActions = actions;
  var rollovers = stampList(advice.superRollovers, rows.funds, 'fundId');
  if (rollovers !== advice.superRollovers) patch.superRollovers = rollovers;
  var coverRows = stampCoverRows(advice[key], rows.covers);
  if (coverRows !== advice[key]) patch[key] = coverRows;
  var explanations = stampExplanations(advice.coverChangeExplanations, rows.covers);
  if (explanations !== advice.coverChangeExplanations) patch.coverChangeExplanations = explanations;
  return Object.keys(patch).length ? Object.assign({}, advice, patch) : advice;
}

/** The record's recommendations / amendments with row ids recorded: { recommendations?, amendments? } or null. */
function stampAdviceRowIds(clientData) {
  var data = clientData || {};
  var rows = rowsOf(data);
  var out = {};
  ['recommendations', 'amendments'].forEach(function (ns) {
    var next = stampAdvice(data[ns], rows, ns);
    if (next !== data[ns]) out[ns] = next;
  });
  return Object.keys(out).length ? out : null;
}

var STAMPED_FIELDS = { insuranceActions: 1, superRollovers: 1, recommendedCovers: 1, recommendedInsuranceCovers: 1, coverChangeExplanations: 1 };

/**
 * A save's updates with row ids recorded on the advice they write, against the rows the save leaves
 * (its own insurance / superannuation when it writes them, else the record's). Handles whole
 * sections ({ recommendations: {…} }) and dot paths ('recommendations.insuranceActions').
 */
function stampAdviceUpdates(updates, clientData) {
  if (!updates || typeof updates !== 'object') return updates;
  var data = clientData || {};
  var covers = updates['insurance.covers'] || (updates.insurance && updates.insurance.covers) || rowsOf(data).covers;
  var funds = updates['superannuation.currentFunds'] || (updates.superannuation && updates.superannuation.currentFunds) || rowsOf(data).funds;
  var rows = { covers: covers, funds: funds };
  var out = null;
  Object.keys(updates).forEach(function (key) {
    var value = updates[key];
    var next = value;
    if ((key === 'recommendations' || key === 'amendments') && value && typeof value === 'object') {
      next = stampAdvice(value, rows, key);
    } else {
      var m = /^(recommendations|amendments)\.(\w+)$/.exec(key);
      if (m && STAMPED_FIELDS[m[2]]) {
        var holder = {};
        holder[m[2]] = value;
        next = stampAdvice(holder, rows, m[1])[m[2]];
      }
    }
    if (next !== value) {
      if (!out) out = Object.assign({}, updates);
      out[key] = next;
    }
  });
  return out || updates;
}

// ---- align ----

/** The row id each old position was recorded with, from every source; null on a disagreement. */
function recordedCoverIds(advice, ns) {
  var ids = [];
  var clash = false;
  var put = function (i, id) {
    if (id == null || !Number.isInteger(i) || i < 0) return;
    var s = String(id);
    if (ids[i] != null && ids[i] !== s) clash = true;
    ids[i] = s;
  };
  (advice.insuranceActions || []).forEach(function (e, i) { if (e) put(i, e.coverId); });
  Object.keys(advice.coverChangeExplanations || {}).forEach(function (key) {
    var m = CURRENT_KEY.exec(key);
    var e = advice.coverChangeExplanations[key];
    if (m && !m[2] && e && typeof e === 'object') put(Number(m[1]), e.coverId);
  });
  (advice[coversKeyOf(ns)] || []).forEach(function (row) {
    if (!row) return;
    if (row.isRetained === true && row.originalCoverIndex != null) put(Number(row.originalCoverIndex), row.originalCoverId);
    var t = remapping.transferIndexOf(row);
    if (t != null) put(t, row.transferredFromCoverId);
  });
  return clash ? null : ids;
}

function recordedFundIds(advice) {
  var ids = [];
  (advice.superRollovers || []).forEach(function (e, i) { if (e && e.fundId != null) ids[i] = String(e.fundId); });
  return ids;
}

/**
 * A remap (as reviewRowRemap's) from the recorded ids to where those rows are now; identity when
 * nothing moved, nothing was recorded, none of the recorded ids exist any more, or it isn't safe.
 */
function remapFromIds(recorded, rows) {
  var count = rows.length;
  var identity = { map: [], count: count, identity: true, shifted: false };
  if (!recorded) return identity;
  var now = {};
  var duplicate = false;
  rows.forEach(function (row, i) {
    var id = idOf(row);
    if (!id) return;
    if (now[id] != null) duplicate = true;
    now[id] = i;
  });
  if (duplicate) return identity;
  var length = Math.max(recorded.length, count);
  var known = 0;
  var unknown = 0;
  for (var k = 0; k < recorded.length; k += 1) {
    if (recorded[k] == null) continue;
    if (now[recorded[k]] != null) known += 1; else unknown += 1;
  }
  // Nothing recorded, or every recorded row is gone (re-keyed): positions stand, as before.
  if (known === 0) return identity;
  var map = [];
  var taken = {};
  var ok = true;
  for (var i = 0; i < length; i += 1) {
    var to = i;
    if (recorded[i] != null) to = now[recorded[i]] != null ? now[recorded[i]] : -1;
    else if (i >= count) to = -1;
    if (to >= 0) {
      if (taken[to]) ok = false;
      taken[to] = true;
    }
    map.push(to);
  }
  if (!ok) return identity;
  var shifted = !map.every(function (m, j) { return m === j || (m === -1 && j >= count); }) || unknown > 0;
  if (!shifted) return identity;
  return { map: map, count: count, identity: false, shifted: true };
}

/** Current Portfolio entries back over the funds their fundId names (only when every entry has a known, unique id). */
function alignPortfolioEntries(fundProjections, funds) {
  var byFund = fundProjections && fundProjections.currentPortfolioByFund;
  if (!Array.isArray(byFund) || !byFund.length) return fundProjections;
  var at = {};
  var ok = byFund.every(function (entry, i) {
    if (!entry || entry.fundId == null || at[String(entry.fundId)] != null) return false;
    at[String(entry.fundId)] = i;
    return true;
  });
  if (!ok) return fundProjections;
  var inOrder = funds.every(function (fund, i) { return idOf(fund) && at[idOf(fund)] === i; });
  if (inOrder && byFund.length === funds.length) return fundProjections;
  var everyFundKnown = funds.every(function (fund) { return idOf(fund) && at[idOf(fund)] != null; });
  if (!everyFundKnown) return fundProjections;
  var next = funds.map(function (fund) { return byFund[at[idOf(fund)]]; });
  return Object.assign({}, fundProjections, { currentPortfolioByFund: next });
}

/**
 * The client record with recommendations / amendments (and the Current Portfolio entries) put back
 * over the rows their recorded ids name. The same object when nothing moved. Read-side only: never
 * written by itself (the next save of the page writes the aligned advice).
 */
function alignAdviceToRowIds(clientData) {
  var data = clientData || {};
  var rows = rowsOf(data);
  var out = null;
  ['recommendations', 'amendments'].forEach(function (ns) {
    var advice = data[ns];
    if (!advice || typeof advice !== 'object') return;
    var coverRemap = remapFromIds(recordedCoverIds(advice, ns), rows.covers);
    var fundRemap = remapFromIds(recordedFundIds(advice), rows.funds);
    if (!coverRemap.shifted && !fundRemap.shifted) return;
    var patch = remapping.remapAdviceObject(advice, fundRemap, coverRemap, coversKeyOf(ns), { removed: 0 });
    if (!patch) return;
    if (!out) out = Object.assign({}, data);
    out[ns] = Object.assign({}, advice, patch);
  });
  var fp = alignPortfolioEntries(data.fundProjections, rows.funds);
  if (fp !== data.fundProjections) {
    if (!out) out = Object.assign({}, data);
    out.fundProjections = fp;
  }
  return out || data;
}

/**
 * Where a recorded id disagrees with the row now at its position (the sweep and the backfill
 * report these): [{ namespace, link, index, recordedId, rowId }].
 */
function adviceLinkMismatches(clientData) {
  var data = clientData || {};
  var rows = rowsOf(data);
  var out = [];
  ['recommendations', 'amendments'].forEach(function (ns) {
    var advice = data[ns];
    if (!advice || typeof advice !== 'object') return;
    var check = function (link, recorded, list) {
      (recorded || []).forEach(function (id, i) {
        if (id == null) return;
        var rowId = idOf(list[i]);
        if (rowId !== id) out.push({ namespace: ns, link: link, index: i, recordedId: id, rowId: rowId });
      });
    };
    check('cover', recordedCoverIds(advice, ns) || [], rows.covers);
    check('fund', recordedFundIds(advice), rows.funds);
  });
  return out;
}

module.exports = {
  stampAdviceRowIds: stampAdviceRowIds,
  stampAdviceUpdates: stampAdviceUpdates,
  alignAdviceToRowIds: alignAdviceToRowIds,
  adviceLinkMismatches: adviceLinkMismatches,
};
